
import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Loader2, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerProps {
  url?: string | null;
  content?: string | null;
  onDownload?: () => void;
  signatureMode?: boolean;
  onSignaturePositionSelected?: (position: {x: number, y: number}) => void;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ 
  url, 
  content, 
  onDownload,
  signatureMode = false,
  onSignaturePositionSelected
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  
  // For signature placement
  const handlePDFClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!signatureMode || !onSignaturePositionSelected) return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    onSignaturePositionSelected({ x, y });
  };

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);
  }

  function changePage(offset: number) {
    setPageNumber(prevPageNumber => prevPageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  // Clean up error handling and loading state
  useEffect(() => {
    if (url) {
      setIsLoading(true);
      setError(null);
    }
  }, [url]);

  // If we don't have a URL or content, render HTML content
  if (!url && content) {
    return (
      <div className="h-full overflow-auto bg-white p-4">
        <div 
          dangerouslySetInnerHTML={{ __html: content }} 
          className="prose max-w-none" 
        />
      </div>
    );
  }

  // If we have neither, show an error
  if (!url && !content) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100 text-gray-500">
        <div className="text-center">
          <p>No document preview available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* PDF controls */}
      <div className="flex items-center justify-between bg-gray-100 border-b border-gray-200 py-2 px-4">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={previousPage}
            disabled={pageNumber <= 1}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous page</span>
          </Button>
          
          <span className="text-sm">
            {pageNumber}/{numPages || '?'}
          </span>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={nextPage}
            disabled={numPages === null || pageNumber >= numPages}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next page</span>
          </Button>

          <div className="flex items-center gap-1 ml-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setScale(scale => Math.max(scale - 0.1, 0.5))}
              className="h-8 w-8 p-0"
            >
              <ZoomOut className="h-3.5 w-3.5" />
              <span className="sr-only">Zoom out</span>
            </Button>
            
            <span className="text-sm min-w-[40px] text-center">
              {Math.round(scale * 100)}%
            </span>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setScale(scale => Math.min(scale + 0.1, 2.5))}
              className="h-8 w-8 p-0"
            >
              <ZoomIn className="h-3.5 w-3.5" />
              <span className="sr-only">Zoom in</span>
            </Button>
          </div>
        </div>
        
        {onDownload && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={onDownload}
            className="h-8 flex items-center gap-1"
          >
            <Download className="h-4 w-4" />
            <span>Download</span>
          </Button>
        )}
      </div>
      
      {/* PDF viewer */}
      <div 
        className={`flex-1 overflow-auto p-4 flex items-center justify-center ${signatureMode ? 'cursor-crosshair' : ''}`}
        onClick={signatureMode ? handlePDFClick : undefined}
      >
        {url && (
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error('Error loading PDF:', error);
              setError('PDF could not be loaded');
              setIsLoading(false);
            }}
            loading={
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-2" />
                <p>Loading PDF...</p>
              </div>
            }
            error={
              <div className="text-center text-red-500">
                <p>The PDF could not be loaded.</p>
                <p className="text-sm text-gray-500 mt-2">
                  Please make sure the URL is correct and access is allowed.
                </p>
              </div>
            }
          >
            {error ? (
              <div className="text-center text-red-500">{error}</div>
            ) : (
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            )}
          </Document>
        )}
      </div>
      
      {/* Signature mode indicator */}
      {signatureMode && (
        <div className="bg-amber-100 text-amber-800 py-2 px-4 text-center text-sm">
          Click on the location in the document where you want to sign
        </div>
      )}
    </div>
  );
};
